/**
 * Account-deletion "Restore or New" flow (ADR docs/adr-account-deletion-recreation.md).
 *
 * When a sign-in path (password login / OAuth callback / set-password) returns
 * 200 { deleted_pending: true, ... } — a soft-deleted account inside its 30-day
 * window — the backend issues NO session cookie. We capture the returned tokens
 * IN MEMORY ONLY (never persisted, security-baseline §1.3) so the choice screen
 * can call restore / discard, then throw DeletedAccountPendingSignal to route
 * the UI to the choice screen instead of completing sign-in.
 */

export type PendingDeletedAccount = {
  accessToken: string
  refreshToken: string
  deletedAt: string
  hardDeleteAt: string
}

// Control signal (not a real error): thrown by the sign-in paths so the calling
// page routes to /account/restore instead of treating deleted_pending as a
// session or an error.
export class DeletedAccountPendingSignal extends Error {
  constructor() {
    super('Account is pending deletion — choose restore or new.')
    this.name = 'DeletedAccountPendingSignal'
  }
}

export function isDeletedAccountPendingSignal(error: unknown): error is DeletedAccountPendingSignal {
  return error instanceof DeletedAccountPendingSignal
}

// In-memory only — lost on reload (the choice screen then sends the user back to
// sign in, which is correct: the captured tokens are gone).
let pending: PendingDeletedAccount | null = null

export function setPendingDeletedAccount(next: PendingDeletedAccount): void {
  pending = next
}

export function readPendingDeletedAccount(): PendingDeletedAccount | null {
  return pending
}

export function clearPendingDeletedAccount(): void {
  pending = null
}

// Sign-in response shape that may carry the deleted_pending branch (shared by
// /auth/login, /auth/set-password and the OAuth callback).
type MaybeDeletedPending = {
  deleted_pending?: boolean
  deletedAt?: string
  hardDeleteAt?: string
  accessToken?: string
  refreshToken?: string
}

/**
 * If the response is the deleted_pending branch, stash its tokens + dates and
 * return true (the caller then throws DeletedAccountPendingSignal). Otherwise
 * returns false and the normal session flow proceeds.
 */
export function captureIfDeletedPending(response: MaybeDeletedPending | null | undefined): boolean {
  if (!response || response.deleted_pending !== true) return false
  setPendingDeletedAccount({
    accessToken: String(response.accessToken || ''),
    refreshToken: String(response.refreshToken || ''),
    deletedAt: String(response.deletedAt || ''),
    hardDeleteAt: String(response.hardDeleteAt || ''),
  })
  return true
}
